// An optional question composer, not a second patient record or clinical inference engine.
export const MAX_QUESTION_CHARS = 4000;
export const INTAKE_VERSION = "woundos-clinician-case-entry-v1";
export const HISTORY_OPTIONS = Object.freeze([
  ["", "Not supplied"], ["present", "Present"], ["absent", "Absent"],
  ["unknown", "Unknown"], ["not_assessed", "Not assessed"],
].map((row) => Object.freeze(row)));
const text = (id, label) => Object.freeze({ id, label, kind: "text" });
const history = (id, label) => Object.freeze({ id, label, kind: "history" });
const section = (id, title, fields) => Object.freeze({ id, title, fields: Object.freeze(fields) });
export const INTAKE_SECTIONS = Object.freeze([
  section("context", "Encounter and existing assessment", [
    text("assessment_time", "Assessment date and time"), text("age", "Age (no date of birth)"),
    text("setting", "Care setting and available support"),
    text("documented_diagnosis", "Clinician-documented diagnosis (if established)"),
    text("subjective", "Subjective findings and patient concerns"),
    text("objective", "Objective examination findings"), text("vitals", "Vitals (include units and time)"),
  ]),
  section("history", "Medical history, medicines and allergies", [
    history("diabetes", "Diabetes"), history("pad", "Peripheral arterial disease"),
    history("pvd", "Other peripheral vascular disease"), history("venous_disease", "Venous disease"),
    history("ckd", "Chronic kidney disease"), text("diabetes_details", "Diabetes type and management"),
    text("medical_history", "Other relevant medical and surgical history"),
    text("medications", "Medicines, anticoagulation and immunosuppression"),
    text("allergies", "Allergies and relevant reactions"),
  ]),
  section("wound", "One wound: identity and measurements", [
    text("site", "Anatomic site"), text("laterality", "Laterality"), text("onset", "Onset and duration"),
    text("origin", "Reported wound origin or mechanism"), text("shape", "Shape"),
    text("dimensions", "Dimensions (L x W x D, with units and time)"),
    text("post_procedure_dimensions", "Post-procedure dimensions (only if already measured)"),
    text("depth_structures", "Depth and exposed or palpable structures"),
    text("undermining", "Undermining (depth, clock positions and assessment context)"),
    text("tunneling", "Tunneling (depth, direction and assessment context)"),
  ]),
  section("appearance", "Wound bed, drainage and surrounding skin", [
    text("edges", "Wound edges"), text("necrotic_tissue", "Necrotic tissue type and amount"),
    text("exudate", "Exudate type and amount"), text("skin_color", "Surrounding skin color"),
    text("edema", "Peripheral tissue edema"), text("induration", "Peripheral tissue induration"),
    text("granulation", "Granulation tissue"), text("epithelialization", "Epithelialization"),
    text("odor", "Wound odor"), text("pain", "Pain and tenderness"),
  ]),
  section("vascular", "Perfusion, venous assessment and limb measurements", [
    text("vascular_exam", "Vascular examination findings and side"),
    text("arterial_studies", "Arterial tests (side, results, date and reliability)"),
    text("venous_studies", "Venous duplex or reflux findings (side and date)"),
    text("right_girth", "Right limb girth (sites, units, position and time)"),
    text("left_girth", "Left limb girth (sites, units, position and time)"),
  ]),
  section("investigations", "Laboratory, imaging and specimen results", [
    text("cbc", "CBC (include units and collection date)"),
    text("crp", "CRP (include units and collection date)"), text("esr", "ESR (include units and collection date)"),
    text("a1c", "HbA1c (include units and collection date)"),
    text("renal_labs", "Renal function and other relevant laboratory results"),
    text("albumin", "Albumin (include units and collection date)"),
    text("prealbumin", "Prealbumin (include units and collection date)"),
    text("culture", "Culture (specimen, site, collection date and result status)"),
    text("pcr", "PCR (specimen, site, collection date and result status)"),
    text("histology", "Histopathology (specimen, site, date and result status)"),
    text("radiographs", "Radiographs (site, date and report)"),
    text("advanced_imaging", "MRI or other imaging (site, date and report)"),
  ]),
  section("healing", "Nutrition, mobility, offloading and practical capacity", [
    text("nutrition", "Nutritional intake, weight trajectory and assessment"),
    text("mobility", "Mobility, ability to reposition and offload"),
    text("support_surfaces", "Current support surfaces and pressure redistribution"),
    text("offloading", "Current offloading and ability to use it"),
    text("continence", "Moisture and continence factors"),
    text("resources", "Caregiver, dressing, transport and referral availability"),
    text("goals", "Patient goals and preferences"),
  ]),
  section("course", "Treatment already delivered and wound trajectory", [
    text("current_care", "Current wound care and products (exact variants when known)"),
    text("procedures", "Procedures already performed (type, date and findings)"),
    text("progress", "Response, interval change and previous measurements"),
  ]),
]);

// Manual score capture only. No descriptor reproduction or automatic wound staging.
// BWAT 2001 rating form, pp. 3–4, hosted by NSW Agency for Clinical Innovation.
export const BWAT_REFERENCE_URL = "https://aci.health.nsw.gov.au/__data/assets/pdf_file/0010/388243/22.-Bates-Jensen-wound-assessment-tool-BWAT.pdf";
export const BWAT_DOMAINS = Object.freeze([
  ["size", "size"], ["depth", "depth"], ["edges", "edges"], ["undermining", "undermining"],
  ["necrotic_type", "necrotic tissue type"], ["necrotic_amount", "necrotic tissue amount"],
  ["exudate_type", "exudate type"], ["exudate_amount", "exudate amount"],
  ["skin_color", "surrounding skin color"], ["edema", "peripheral tissue edema"],
  ["induration", "peripheral tissue induration"], ["granulation", "granulation tissue"],
  ["epithelialization", "epithelialization"],
].map(([id, label]) => Object.freeze({ id, label })));
const FIELDS = new Map(INTAKE_SECTIONS.flatMap((group) => group.fields.map((field) => [field.id, field])));
const SCORE_IDS = new Set(BWAT_DOMAINS.map((f) => `bwat_${f.id}`));
const HISTORIES = new Set(HISTORY_OPTIONS.map(([id]) => id));

function validString(value, maxLength = 600) {
  if (typeof value !== "string" || value.length > maxLength) throw new Error("Invalid case entry.");
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)) throw new Error("Invalid case entry.");
  for (const character of value) {
    const point = character.codePointAt(0);
    if (point >= 0xd800 && point <= 0xdfff) throw new Error("Invalid case entry.");
  }
  return value;
}

function validate(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)
      || ![Object.prototype, null].includes(Object.getPrototypeOf(record))) {
    throw new Error("Invalid case entry.");
  }
  for (const [id, value] of Object.entries(record)) {
    validString(value);
    if (SCORE_IDS.has(id)) {
      if (value !== "" && !/^[1-5]$/.test(value)) throw new Error("Use the scoring form's 1–5 item scores.");
    } else if (!FIELDS.has(id) || (FIELDS.get(id).kind === "history" && !HISTORIES.has(value))) {
      throw new Error("Invalid case entry.");
    }
  }
}

export function summarizeBwat(record) {
  validate(record);
  const scores = BWAT_DOMAINS.map(({ id }) => record[`bwat_${id}`]).filter((v) => v !== undefined && v !== "");
  return { completed: scores.length, total: scores.length === 13 ? scores.reduce((sum, v) => sum + Number(v), 0) : null };
}

export function bwatSummaryText(record) {
  const { completed, total } = summarizeBwat(record);
  return total === null ? `BWAT incomplete (${completed}/13 items); no total calculated.`
    : `BWAT total: ${total}/65 (13/13 clinician-entered scores).`;
}

function historySentence(label, value) {
  if (value === "present" || value === "absent") return `${label} is ${value}.`;
  if (value === "unknown") return `${label} status is unknown.`;
  return `${label} was not assessed.`;
}
const terminate = (value) => /[.!?;]$/.test(value) ? value : `${value}.`;

export function buildCaseText(record) {
  validate(record);
  const lines = [];
  for (const group of INTAKE_SECTIONS) {
    for (const field of group.fields) {
      const value = record[field.id]?.trim();
      if (!value) continue;
      lines.push(field.kind === "history" ? historySentence(field.label, value)
        : `${field.label}: ${terminate(value)}`);
    }
  }
  const score = summarizeBwat(record);
  if (score.completed) {
    lines.push("Clinician-entered BWAT instrument scores:");
    for (const { id, label } of BWAT_DOMAINS) {
      if (record[`bwat_${id}`]) lines.push(`BWAT ${label} score: ${record[`bwat_${id}`]}/5.`);
    }
    lines.push(bwatSummaryText(record));
  }
  return lines.length ? `Clinician-entered observations for one wound.\n${lines.join("\n")}` : "";
}

export function buildCaseDraft(question, record) {
  if (typeof question !== "string") throw new Error("Invalid question.");
  if (question.length > MAX_QUESTION_CHARS) throw new Error("The question exceeds 4000 characters; nothing was removed.");
  validString(question, MAX_QUESTION_CHARS);
  const details = buildCaseText(record);
  const composed = [question.trim(), details].filter(Boolean).join("\n\n");
  if (composed.length > MAX_QUESTION_CHARS) {
    throw new Error("The combined question exceeds 4000 characters. Shorten the draft; no observations have been removed.");
  }
  return composed;
}
